/**
 * 订单管理页
 *
 * 单独管理有效按点计费订单：待确认、已生效。
 * 已取消订单不再保留在列表中。
 * 支持创建、编辑、删除、确认到账。
 */

import { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  AlertColor,
  Chip,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Grid,
} from '@mui/material'
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid'
import AddIcon from '@mui/icons-material/Add'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { AdminLayout } from '@/components/AdminLayout'
import {
  getBrands,
  createSubscription,
  confirmPayment,
  getSubscriptions,
  updateSubscription,
  deleteSubscription,
} from '@/api/services'
import type { Brand, Subscription } from '@chaoyinli/shared'
import { formatDate, formatDateOnly, formatCurrency } from '@/utils/format'

/** 订单状态标签 */
const ORDER_STATUS_LABELS: Record<string, string> = {
  pending_payment: '待付款',
  paid: '已付款',
  active: '已生效',
  expired: '已过期',
  cancelled: '已取消',
}

const ORDER_STATUS_COLORS: Record<string, string> = {
  pending_payment: '#FF9800',
  paid: '#2196F3',
  active: '#4CAF50',
  expired: '#F44336',
  cancelled: '#999',
}

const INVOICE_STATUS_LABELS: Record<string, string> = {
  none: '未申请',
  pending: '处理中',
  issued: '已开票',
}

export function OrderManagementPage() {
  // 品牌列表
  const [brands, setBrands] = useState<Brand[]>([])
  const [loadingBrands, setLoadingBrands] = useState(true)

  // 筛选
  const [filterBrandId, setFilterBrandId] = useState('')
  const [filterStatus, setFilterStatus] = useState('')

  // 订单列表
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [loadingSubs, setLoadingSubs] = useState(true)

  // 创建订阅弹窗
  const [createOpen, setCreateOpen] = useState(false)
  const [selectedBrandId, setSelectedBrandId] = useState('')
  const [unitPrice, setUnitPrice] = useState('3')
  const [quotaGranted, setQuotaGranted] = useState('500')
  const [creating, setCreating] = useState(false)

  // 确认到账弹窗
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingOrder, setPendingOrder] = useState<Subscription | null>(null)
  const [startDate, setStartDate] = useState('')
  const [confirming, setConfirming] = useState(false)

  // 编辑弹窗
  const [editOpen, setEditOpen] = useState(false)
  const [editingOrder, setEditingOrder] = useState<Subscription | null>(null)
  const [editUnitPrice, setEditUnitPrice] = useState('')
  const [editQuotaGranted, setEditQuotaGranted] = useState('')
  const [editStartDate, setEditStartDate] = useState('')
  const [editContractUrl, setEditContractUrl] = useState('')
  const [editInvoiceStatus, setEditInvoiceStatus] = useState('none')
  const [editing, setEditing] = useState(false)

  // 删除弹窗
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deletingOrder, setDeletingOrder] = useState<Subscription | null>(null)
  const [deleting, setDeleting] = useState(false)

  // Toast
  const [toast, setToast] = useState<{ open: boolean; message: string; severity: AlertColor }>({
    open: false,
    message: '',
    severity: 'info',
  })

  const showToast = (message: string, severity: AlertColor = 'info') => {
    setToast({ open: true, message, severity })
  }

  const handleCloseToast = () => {
    setToast((prev) => ({ ...prev, open: false }))
  }

  // 加载品牌列表
  const fetchBrands = useCallback(async () => {
    setLoadingBrands(true)
    try {
      const data = await getBrands()
      setBrands(data)
    } catch (e) {
      showToast(e instanceof Error ? e.message : '加载品牌列表失败', 'error')
    } finally {
      setLoadingBrands(false)
    }
  }, [])

  // 加载订单列表
  const fetchSubscriptions = useCallback(async () => {
    setLoadingSubs(true)
    try {
      const params: Record<string, string | number> = { page_size: 100 }
      if (filterBrandId) params.brand_id = filterBrandId
      if (filterStatus) params.status = filterStatus
      const data = await getSubscriptions(params)
      setSubscriptions(data.items || [])
    } catch (e) {
      showToast(e instanceof Error ? e.message : '加载订单列表失败', 'error')
    } finally {
      setLoadingSubs(false)
    }
  }, [filterBrandId, filterStatus])

  useEffect(() => {
    fetchBrands()
    fetchSubscriptions()
  }, [fetchBrands, fetchSubscriptions])

  const getBrandName = useCallback(
    (brandId: string) => {
      return brands.find((b) => b.id === brandId)?.name || brandId
    },
    [brands],
  )

  // 创建订单
  const handleCreate = () => {
    setSelectedBrandId('')
    setUnitPrice('3')
    setQuotaGranted('500')
    setCreateOpen(true)
  }

  const handleCreateSubmit = async () => {
    if (!selectedBrandId) {
      showToast('请选择品牌', 'warning')
      return
    }

    setCreating(true)
    try {
      const sub = await createSubscription({
        brand_id: selectedBrandId,
        unit_price: Math.round((Number(unitPrice) || 0) * 100),
        quota_granted: Number(quotaGranted) || 0,
      })
      showToast('订单已创建', 'success')
      setCreateOpen(false)
      await fetchSubscriptions()

      if (sub.status === 'pending_payment') {
        setPendingOrder(sub)
        setStartDate('')
        setConfirmOpen(true)
      }
    } catch (e) {
      showToast(e instanceof Error ? e.message : '创建订单失败', 'error')
    } finally {
      setCreating(false)
    }
  }

  // 确认到账
  const handleOpenConfirm = (sub: Subscription) => {
    setPendingOrder(sub)
    setStartDate('')
    setConfirmOpen(true)
  }

  const handleConfirm = async () => {
    if (!pendingOrder) return

    setConfirming(true)
    try {
      const data = startDate ? { start_date: startDate } : {}
      await confirmPayment(pendingOrder.id, data)
      await fetchSubscriptions()
      showToast('到账已确认', 'success')
      setConfirmOpen(false)
    } catch (e) {
      showToast(e instanceof Error ? e.message : '确认失败', 'error')
    } finally {
      setConfirming(false)
    }
  }

  // 编辑订单
  const handleOpenEdit = (sub: Subscription) => {
    setEditingOrder(sub)
    setEditUnitPrice(String((sub.unit_amount ?? 0) / 100))
    setEditQuotaGranted(String(sub.quota_granted ?? 0))
    setEditStartDate(sub.start_date ? formatDateOnly(sub.start_date) : '')
    setEditContractUrl(sub.contract_url || '')
    setEditInvoiceStatus(sub.invoice_status || 'none')
    setEditOpen(true)
  }

  const handleEditSubmit = async () => {
    if (!editingOrder) return

    setEditing(true)
    try {
      const payload: Record<string, string | number> = {}
      const unitAmount = Math.round((Number(editUnitPrice) || 0) * 100)
      const quotaGranted = Number(editQuotaGranted) || 0
      if (unitAmount > 0) payload.unit_amount = unitAmount
      if (quotaGranted > 0) payload.quota_granted = quotaGranted
      if (editStartDate) payload.start_date = editStartDate
      if (editContractUrl) payload.contract_url = editContractUrl
      if (editInvoiceStatus) payload.invoice_status = editInvoiceStatus

      await updateSubscription(editingOrder.id, payload)
      await fetchSubscriptions()
      showToast('订单编辑成功', 'success')
      setEditOpen(false)
    } catch (e) {
      showToast(e instanceof Error ? e.message : '编辑失败', 'error')
    } finally {
      setEditing(false)
    }
  }

  // 删除/取消订单
  const handleOpenDelete = (sub: Subscription) => {
    setDeletingOrder(sub)
    setDeleteOpen(true)
  }

  const handleDeleteSubmit = async () => {
    if (!deletingOrder) return

    setDeleting(true)
    try {
      await deleteSubscription(deletingOrder.id)
      await fetchSubscriptions()
      showToast('订单已取消', 'success')
      setDeleteOpen(false)
    } catch (e) {
      showToast(e instanceof Error ? e.message : '取消失败', 'error')
    } finally {
      setDeleting(false)
    }
  }

  // DataGrid 列定义
  const columns: GridColDef[] = [
    {
      field: 'order_number',
      headerName: '订单号',
      width: 160,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: '12px', color: '#666' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'brand_name',
      headerName: '品牌',
      width: 160,
      valueGetter: (_value, row) => getBrandName(row.brand_id as string),
    },
    {
      field: 'quota_granted',
      headerName: '额度',
      width: 100,
      renderCell: (params) => (
        <Chip label={`${params.value ?? 0} 点`} size="small" sx={{ fontSize: '12px' }} />
      ),
    },
    {
      field: 'unit_amount',
      headerName: '单价',
      width: 100,
      valueGetter: (value) => `¥${((value as number) ?? 0) / 100}`,
    },
    {
      field: 'amount',
      headerName: '金额',
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: '#FF8C00', fontWeight: 600 }}>
          {formatCurrency((params.value ?? 0) / 100)}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: '状态',
      width: 100,
      renderCell: (params) => {
        const color = ORDER_STATUS_COLORS[params.value as string] || '#999'
        return (
          <Chip
            label={ORDER_STATUS_LABELS[params.value as string] || params.value}
            size="small"
            sx={{ backgroundColor: `${color}15`, color, fontSize: '12px' }}
          />
        )
      },
    },
    {
      field: 'invoice_status',
      headerName: '开票',
      width: 100,
      valueGetter: (value) => INVOICE_STATUS_LABELS[value as string] || value,
    },
    {
      field: 'created_at',
      headerName: '创建时间',
      width: 150,
      valueGetter: (value) => formatDate(value as string),
    },
    {
      field: 'actions',
      headerName: '操作',
      width: 160,
      sortable: false,
      type: 'actions',
      getActions: (params) => {
        const sub = params.row as Subscription
        const actions: React.ReactElement[] = []
        if (sub.status === 'pending_payment') {
          actions.push(
            <GridActionsCellItem
              key="confirm"
              icon={
                <Tooltip title="确认到账">
                  <CheckCircleIcon fontSize="small" sx={{ color: '#4CAF50' }} />
                </Tooltip>
              }
              label="确认到账"
              onClick={() => handleOpenConfirm(sub)}
            />,
            <GridActionsCellItem
              key="edit"
              icon={
                <Tooltip title="编辑">
                  <EditIcon fontSize="small" sx={{ color: '#2196F3' }} />
                </Tooltip>
              }
              label="编辑"
              onClick={() => handleOpenEdit(sub)}
            />,
            <GridActionsCellItem
              key="delete"
              icon={
                <Tooltip title="取消订单">
                  <DeleteIcon fontSize="small" sx={{ color: '#F44336' }} />
                </Tooltip>
              }
              label="取消订单"
              onClick={() => handleOpenDelete(sub)}
            />,
          )
        }
        return actions
      },
    },
  ]

  const pendingOrders = subscriptions.filter((s) => s.status === 'pending_payment')
  const selectedBrandName = brands.find((b) => b.id === selectedBrandId)?.name || ''

  return (
    <AdminLayout>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
          订单管理
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
          sx={{
            backgroundColor: '#FF8C00',
            textTransform: 'none',
            '&:hover': { backgroundColor: '#E67E00' },
          }}
        >
          创建订单
        </Button>
      </Box>

      {/* 筛选栏 */}
      <Card sx={{ borderRadius: '12px', mb: 3 }}>
        <CardContent sx={{ p: 2.5, pb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                select
                label="品牌"
                value={filterBrandId}
                onChange={(e) => setFilterBrandId(e.target.value)}
                size="small"
                fullWidth
              >
                <MenuItem value="">全部品牌</MenuItem>
                {brands.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                    {b.sub_brand ? ` - ${b.sub_brand}` : ''}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                label="状态"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                size="small"
                fullWidth
              >
                <MenuItem value="">全部有效</MenuItem>
                <MenuItem value="pending_payment">待付款</MenuItem>
                <MenuItem value="active">已生效</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="outlined"
                onClick={fetchSubscriptions}
                sx={{ color: '#FF8C00', borderColor: '#FF8C00', textTransform: 'none' }}
              >
                刷新
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* 待确认订单区域 */}
      {filterStatus !== 'active' && pendingOrders.length > 0 && (
        <Card sx={{ borderRadius: '12px', mb: 3, border: '1px solid #FFE0B2' }}>
          <CardContent sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#FF9800', mb: 1.5 }}>
              ⏳ 待确认到账的订单
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#fafafa' }}>
                    <TableCell sx={{ fontWeight: 600, color: '#666' }}>订单号</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#666' }}>品牌</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#666' }}>方案</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#666' }}>金额</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#666' }}>创建时间</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#666' }}>操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingOrders.map((sub) => {
                    const brandName = getBrandName(sub.brand_id)
                    return (
                      <TableRow key={sub.id}>
                        <TableCell sx={{ fontSize: '12px', color: '#666' }}>{sub.order_number}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#333' }}>{brandName}</TableCell>
                        <TableCell>
                          <Chip label={`${sub.quota_granted ?? 0} 点`} size="small" sx={{ fontSize: '12px' }} />
                        </TableCell>
                        <TableCell sx={{ color: '#FF8C00', fontWeight: 600 }}>
                          {formatCurrency((sub.amount ?? 0) / 100)}
                        </TableCell>
                        <TableCell sx={{ color: '#999' }}>{formatDate(sub.created_at)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton size="small" onClick={() => handleOpenConfirm(sub)} sx={{ color: '#4CAF50' }}>
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleOpenEdit(sub)} sx={{ color: '#2196F3' }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleOpenDelete(sub)} sx={{ color: '#F44336' }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* 全部订单列表 */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333', mb: 1.5 }}>
        全部订单
      </Typography>
      <Box
        sx={{
          height: 'calc(100vh - 420px)',
          minHeight: 300,
          backgroundColor: '#fff',
          borderRadius: '12px',
        }}
      >
        {loadingSubs || loadingBrands ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#FF8C00' }} />
          </Box>
        ) : (
          <DataGrid
            rows={subscriptions}
            columns={columns}
            getRowId={(row) => row.id}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell:focus': { outline: 'none' },
              '& .MuiDataGrid-columnHeader': { backgroundColor: '#fafafa' },
            }}
          />
        )}
      </Box>

      {/* 创建订单弹窗 */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>创建订单</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              select
              label="选择品牌"
              value={selectedBrandId}
              onChange={(e) => setSelectedBrandId(e.target.value)}
              size="small"
              fullWidth
            >
              {brands.map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name}
                  {b.sub_brand ? ` - ${b.sub_brand}` : ''}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="单点金额（元）"
              type="number"
              value={unitPrice}
              onChange={(e) => setUnitPrice(e.target.value)}
              size="small"
              fullWidth
              helperText="每点记录的单价，仅作记录不直接扣费"
            />
            <TextField
              label="授予点数"
              type="number"
              value={quotaGranted}
              onChange={(e) => setQuotaGranted(e.target.value)}
              size="small"
              fullWidth
              helperText="本次订单授予该品牌的总生成点数（所有员工共用）"
            />

            <Card variant="outlined" sx={{ borderRadius: '8px' }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  预计应付总额：
                  <Box component="span" sx={{ color: '#FF8C00', fontWeight: 600, ml: 1, fontSize: '16px' }}>
                    ¥
                    {(
                      Math.round(
                        (Number(unitPrice) || 0) * (Number(quotaGranted) || 0) * 100,
                      ) / 100
                    ).toLocaleString()}
                  </Box>
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setCreateOpen(false)} sx={{ color: '#666' }}>
            取消
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateSubmit}
            disabled={creating || !selectedBrandId}
            sx={{
              backgroundColor: '#FF8C00',
              '&:hover': { backgroundColor: '#E67E00' },
            }}
          >
            {creating ? '创建中...' : '创建订单'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 确认到账弹窗 */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>确认到账</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {pendingOrder && (
              <>
                <Box sx={{ p: 2, backgroundColor: '#FFF8F0', borderRadius: '8px' }}>
                  <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                    订单号：{pendingOrder.order_number}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                    额度：{pendingOrder.quota_granted ?? 0} 点 · 单价 ¥
                    {((pendingOrder.unit_amount ?? 0) / 100).toFixed(2)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#FF8C00', fontWeight: 600 }}>
                    金额：{formatCurrency((pendingOrder.amount ?? 0) / 100)}
                  </Typography>
                </Box>
                <TextField
                  label="生效日期（可选）"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  helperText="不填则从今天开始计算"
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmOpen(false)} sx={{ color: '#666' }}>
            取消
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={confirming}
            startIcon={<CheckCircleIcon />}
            sx={{
              backgroundColor: '#4CAF50',
              '&:hover': { backgroundColor: '#43A047' },
            }}
          >
            {confirming ? '确认中...' : '确认到账'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 编辑弹窗 */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>编辑订单</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {editingOrder && (
              <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  订单号：{editingOrder.order_number}
                </Typography>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  品牌：{getBrandName(editingOrder.brand_id)}
                </Typography>
              </Box>
            )}
            <TextField
              label="单点金额（元）"
              type="number"
              value={editUnitPrice}
              onChange={(e) => setEditUnitPrice(e.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              label="授予点数"
              type="number"
              value={editQuotaGranted}
              onChange={(e) => setEditQuotaGranted(e.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              label="生效日期（可选）"
              type="date"
              value={editStartDate}
              onChange={(e) => setEditStartDate(e.target.value)}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="合同/报价单 URL"
              type="url"
              value={editContractUrl}
              onChange={(e) => setEditContractUrl(e.target.value)}
              size="small"
              fullWidth
            />
            <TextField
              select
              label="开票状态"
              value={editInvoiceStatus}
              onChange={(e) => setEditInvoiceStatus(e.target.value)}
              size="small"
              fullWidth
            >
              <MenuItem value="none">未申请</MenuItem>
              <MenuItem value="pending">处理中</MenuItem>
              <MenuItem value="issued">已开票</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEditOpen(false)} sx={{ color: '#666' }}>
            取消
          </Button>
          <Button
            variant="contained"
            onClick={handleEditSubmit}
            disabled={editing}
            sx={{
              backgroundColor: '#2196F3',
              '&:hover': { backgroundColor: '#1976D2' },
            }}
          >
            {editing ? '保存中...' : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 删除确认弹窗 */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>取消订单</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
            确定要取消订单 <strong>{deletingOrder?.order_number}</strong> 吗？
          </Typography>
          <Typography variant="body2" sx={{ color: '#999', mt: 1 }}>
            取消后订单状态将变为「已取消」，仅待付款订单可取消。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteOpen(false)} sx={{ color: '#666' }}>
            取消
          </Button>
          <Button
            variant="contained"
            onClick={handleDeleteSubmit}
            disabled={deleting}
            sx={{
              backgroundColor: '#F44336',
              '&:hover': { backgroundColor: '#D32F2F' },
            }}
          >
            {deleting ? '取消中...' : '确认取消'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity} variant="filled">
          {toast.message}
        </Alert>
      </Snackbar>
    </AdminLayout>
  )
}

export default OrderManagementPage
